import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';

type FolderRef = { id: string; name: string };
type BoxFolder = FolderRef & { path: FolderRef[]; folders: FolderRef[] };
type Result = { at: string; ok: boolean; error: string | null };
/** `GET /api/backup/archive` (owner only): never the password — only whether one is saved and when. */
export type ArchiveView = {
  enabled: boolean;
  folder: FolderRef | null;
  passwordSet: boolean;
  passwordSavedAt: string | null;
  passwordError: string | null;
  running: { since: string; step: string } | null;
  last: { katalog: Result | null; kopia: Result | null };
};

const ARCHIVE_KEY = ['backup', 'archive'] as const;
const WHEN = new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const errorText = (e: unknown, fallback: string) => {
  if (!(e instanceof ApiError)) return fallback;
  const issues = fieldIssues(e);
  return issues.length > 0 ? issues.map((i) => i.message).join(' · ') : e.message;
};
const folderName = (f: FolderRef) => (f.id === '0' ? 'Wszystkie pliki' : f.name);
const LABELS = { katalog: 'Katalog (Excel)', kopia: 'Pełna kopia programu' } as const;

/**
 * „Archiwum w Box” on the Box card (owner 2026-11-04): the archive folder, the copies' password (typed twice,
 * write-only), the last result of each of the two nightly files and „Zrób kopię teraz”. No notifications (owner 20:58).
 */
export function BoxArchiveSettings({ pollMs = 3000 }: { pollMs?: number }) {
  const queryClient = useQueryClient();
  const archive = useQuery({
    queryKey: ARCHIVE_KEY,
    queryFn: () => api<ArchiveView>('/api/backup/archive'),
    // a copy in progress: asked again until it is over
    refetchInterval: (q) => (q.state.data?.running ? pollMs : false),
  });
  const view = archive.data;
  const [browsing, setBrowsing] = useState<string | null>(null);
  const [link, setLink] = useState('');
  const [form, setForm] = useState<{ password: string; again: string } | null>(null);
  const [mismatch, setMismatch] = useState(false);
  const done = (v: ArchiveView) => {
    queryClient.setQueryData(ARCHIVE_KEY, v);
    setBrowsing(null);
    setLink('');
    setForm(null);
    setMismatch(false);
  };
  const chooseFolder = useMutation({ mutationFn: (folder: string) => api<ArchiveView>('/api/backup/archive/folder', { method: 'PUT', body: { folder } }), onSuccess: done });
  const savePassword = useMutation({ mutationFn: (password: string) => api<ArchiveView>('/api/backup/archive/password', { method: 'PUT', body: { password } }), onSuccess: done });
  const runNow = useMutation({ mutationFn: () => api<ArchiveView>('/api/backup/archive/run', { method: 'POST' }), onSuccess: (v) => queryClient.setQueryData(ARCHIVE_KEY, v) });
  const folder = useQuery({ queryKey: ['box', 'settings', 'folders', browsing], queryFn: () => api<BoxFolder>(`/api/box/folders?parent=${encodeURIComponent(browsing!)}`), enabled: browsing !== null, retry: false });
  const busy = chooseFolder.isPending || savePassword.isPending;
  const editingPassword = view && (!view.passwordSet || form !== null);
  const values = form ?? { password: '', again: '' };

  function submitPassword(e: FormEvent) {
    e.preventDefault();
    if (values.password !== values.again) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    savePassword.mutate(values.password);
  }

  return (
    <div className="settings-subsection" aria-label="Archiwum w Box" role="group" data-feature-id="backup.archive">
      <h3>Archiwum w Box</h3>
      <p className="hint">Co noc (ok. 3:30) do folderu archiwum trafiają dwa pliki: katalog w Excelu i pełna kopia programu zaszyfrowana hasłem do kopii. Pliki starsze niż 60 dni program sam usuwa.</p>
      {archive.error && (
        <p className="alert" role="alert">
          {errorText(archive.error, 'Nie udało się wczytać ustawień archiwum')}
        </p>
      )}
      {view && (
        <>
          {!view.enabled && <p className="notice">Archiwum wyłączone – ustaw folder i hasło</p>}
          <p>
            <span>{`Folder na archiwum: ${view.folder ? view.folder.name : 'nie wybrano'}`}</span>{' '}
            {browsing === null && (
              <button className="link-button" type="button" onClick={() => setBrowsing(view.folder?.id ?? '0')}>
                Wskaż folder
              </button>
            )}
          </p>
          {browsing !== null && (
            <div className="settings-form">
              {folder.error && (
                <p className="alert" role="alert">
                  {errorText(folder.error, 'Nie udało się wczytać folderów z Box')}
                </p>
              )}
              {folder.data && (
                <>
                  <p>
                    {[...folder.data.path, folder.data].map((f, i, all) =>
                      i < all.length - 1 ? (
                        <span key={f.id}>
                          <button className="link-button" type="button" onClick={() => setBrowsing(f.id)}>
                            {folderName(f)}
                          </button>
                          {' / '}
                        </span>
                      ) : (
                        <strong key={f.id}>{folderName(f)}</strong>
                      ),
                    )}
                  </p>
                  {folder.data.folders.length === 0 ? (
                    <p className="muted">{folder.data.id === '0' ? 'Aplikacja nie widzi żadnego folderu – udostępnij jej folder w Box.' : 'Brak podfolderów.'}</p>
                  ) : (
                    <ul className="settings-list">
                      {folder.data.folders.map((f) => (
                        <li key={f.id} className="settings-row">
                          <button className="link-button" type="button" onClick={() => setBrowsing(f.id)}>
                            {f.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="actions">
                    <button className="btn primary" type="button" disabled={busy || folder.data.id === '0'} onClick={() => chooseFolder.mutate(folder.data!.id)}>
                      Wybierz ten folder
                    </button>
                  </div>
                </>
              )}
              <div className="field-row">
                <label className="field grow">
                  <span>Link do folderu archiwum w Box</span>
                  <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://….box.com/folder/123…" spellCheck={false} />
                </label>
                <div className="actions">
                  <button className="btn" type="button" disabled={busy || link.trim() === ''} onClick={() => chooseFolder.mutate(link.trim())}>
                    Zapisz folder archiwum
                  </button>
                </div>
              </div>
              <button
                className="link-button"
                type="button"
                onClick={() => {
                  setBrowsing(null);
                  chooseFolder.reset();
                }}
              >
                Anuluj
              </button>
              {chooseFolder.error && (
                <p className="alert" role="alert">
                  {errorText(chooseFolder.error, 'Nie udało się zapisać folderu')}
                </p>
              )}
            </div>
          )}
          {view.passwordError && (
            <p className="alert" role="alert">
              {view.passwordError}
            </p>
          )}
          {editingPassword ? (
            <form className="settings-form" onSubmit={submitPassword}>
              <label className="field">
                <span>Hasło do kopii</span>
                <input type="password" value={values.password} onChange={(e) => setForm({ ...values, password: e.target.value })} autoComplete="new-password" spellCheck={false} />
              </label>
              <label className="field">
                <span>Powtórz hasło</span>
                <input type="password" value={values.again} onChange={(e) => setForm({ ...values, again: e.target.value })} autoComplete="new-password" spellCheck={false} />
              </label>
              <p className="muted">Zapisz hasło w bezpiecznym miejscu – program go nie pokaże, a bez niego kopii nie da się otworzyć.</p>
              {mismatch && (
                <p className="alert" role="alert">
                  Hasła się różnią
                </p>
              )}
              {savePassword.error && (
                <p className="alert" role="alert">
                  {errorText(savePassword.error, 'Nie udało się zapisać hasła')}
                </p>
              )}
              <div className="actions">
                <button className="btn primary" type="submit" disabled={busy}>
                  {savePassword.isPending ? 'Zapisywanie…' : 'Zapisz hasło'}
                </button>
                {view.passwordSet && (
                  <button
                    className="link-button"
                    type="button"
                    onClick={() => {
                      setForm(null);
                      setMismatch(false);
                      savePassword.reset();
                    }}
                  >
                    Anuluj
                  </button>
                )}
              </div>
            </form>
          ) : (
            <p>
              <span>{`Hasło do kopii zapisane ${view.passwordSavedAt ? WHEN.format(new Date(view.passwordSavedAt)) : ''}`.trim()}</span>{' '}
              <button className="link-button" type="button" onClick={() => setForm({ password: '', again: '' })}>
                Zmień hasło
              </button>
            </p>
          )}
          {(['katalog', 'kopia'] as const).map((kind) => {
            const r = view.last[kind];
            if (!r) return null;
            return <p key={kind}>{`${LABELS[kind]} – ostatnia kopia: ${WHEN.format(new Date(r.at))} – ${r.ok ? 'OK' : `błąd: ${r.error ?? ''}`}`}</p>;
          })}
          {view.running ? (
            <p className="muted">{`Kopia w toku: ${view.running.step}…`}</p>
          ) : (
            view.enabled && (
              <p>
                <button className="link-button" type="button" disabled={runNow.isPending} onClick={() => runNow.mutate()}>
                  Zrób kopię teraz
                </button>
              </p>
            )
          )}
          {runNow.error && (
            <p className="alert" role="alert">
              {errorText(runNow.error, 'Nie udało się rozpocząć kopii')}
            </p>
          )}
        </>
      )}
    </div>
  );
}
